import Box from 'UIKit/Box/Box'
import Popper from 'UIKit/Popper'

import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useToggle from 'hooks/useToggle'
import { RowCenter } from 'layout/Components/Row'
import React, { useEffect, useState } from 'react'
import { animated, useSpring } from 'react-spring'
import styled, { css } from 'styled-components'
import { Icons } from 'svgs'
import { breakpointMap } from 'theme/base'
import { BoxProps } from 'UIKit/Box/types'
import IconButton from 'UIKit/Button/IconButton'
import Image from 'UIKit/Image'
import { StyledMenuDropdownContainer } from 'UIKit/Popper/Popper'
import Text from 'UIKit/Text'
import TransText from 'UIKit/Text/TransText'
import ProviderFilterContainer from './components/ProviderFilterContainer'
import { useOpenProviderFilter } from './hooks'

type ProviderFilterButtonProps = {
  selectedProviders: string[]
  onProvidersChanged: (codes: string[]) => void
}

const ProviderFilter: React.FC<ProviderFilterButtonProps & BoxProps> = ({
  selectedProviders,
  onProvidersChanged,
  ...props
}) => {
  const [toggle, setToggle] = useToggle(false)
  const { handlePresentProviderFilterModal } = useOpenProviderFilter()
  const { isMobile } = useMatchBreakpoints()
  const numberOfSelected = selectedProviders.length

  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(false)

  useEffect(() => {
    if (toggle && !isMobile) {
      setShouldRenderDropdown(true)
    }
  }, [toggle, isMobile, shouldRenderDropdown])

  const dropdownSpring = useSpring({
    opacity: toggle ? 1 : 0,
    transform: toggle ? 'translateY(0px)' : 'translateY(20px)',
    config: { tension: 300, friction: 24, duration: 150 },
    onResolve: () => {
      if (!toggle) {
        setShouldRenderDropdown(false)
      }
    },
  })

  const handleOpenProviderFilterModal = () => {
    if (!isMobile) {
      return
    }

    handlePresentProviderFilterModal({
      selectedProviders,
      onProvidersChanged,
    })
  }

  return (
    <StyledContainer {...props}>
      <Popper
        open={shouldRenderDropdown && !isMobile}
        setOpen={setToggle}
        dropdownContent={
          <animated.div style={dropdownSpring}>
            <ProviderFilterContainer
              selectedProviders={selectedProviders}
              onProvidersChanged={onProvidersChanged}
              submitCallback={setToggle}
            />
          </animated.div>
        }
        dropdownPosition="bottom-right"
        id="provider-selected"
        className="provider-selector"
        menuDropdownContainerStyle={{
          boxShadow: '0px 8px 8px 0px rgba(0, 0, 0, 0.4)',
          borderRadius: '22px',
          maxHeight: 'none',
          overflow: 'visible',
          marginTop: '6px',
        }}
      >
        <StyledWrapper
          $active={numberOfSelected > 0}
          amount={numberOfSelected}
          maxHeight="100%"
          height="100%"
          onClick={handleOpenProviderFilterModal}
        >
          <Box mr="4px">
            <Image src="/images/providers-icon.png" width={16} height={16} />
          </Box>

          <TransText fontSize="12px" fontWeight={500} color="textSubtle">
            All Providers
          </TransText>

          <StyledArrowIconContainer $isOpen={toggle && !isMobile}>
            <Icons.ArrowDownIcon width={20} height={20} />
          </StyledArrowIconContainer>

          {!!numberOfSelected && (
            <RowCenter ml="4px" px="6px" height="16px" borderRadius="100px" background="#fff">
              <Text fontSize="12px" fontWeight={600} color="#000">
                {numberOfSelected}
              </Text>
            </RowCenter>
          )}
        </StyledWrapper>
      </Popper>
    </StyledContainer>
  )
}

export const StyledArrowIconContainer = styled(RowCenter)<{ $isOpen: boolean }>`
  transition: ${({ theme }) => theme.transitions.fast};
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : '')};
  fill: ${({ theme }) => theme.colors.textSubtle} !important;
`

const StyledContainer = styled(Box)`
  ${StyledMenuDropdownContainer} {
    margin-top: 8px;
    border-radius: 8px;
    overflow: hidden;

    @media (max-width: ${breakpointMap['md']}px) {
      right: 0px !important;
      left: unset !important;
    }
  }
`

const StyledWrapper = styled(IconButton)<{ $active: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: ${({ theme }) => theme.radii.button};
  color: ${({ theme }) => theme.colors.textSubtle};
  padding: 0px 10px;
  background: ${({ theme }) => theme.colors.card_primary};

  ${({ $active }) =>
    $active &&
    css`
      border-color: ${({ theme }) => theme.colors.text};
      color: ${({ theme }) => theme.colors.text};
      svg path {
        fill: ${({ theme }) => theme.colors.text}; !important;
      }
    `}
  ${({ theme }) => theme.mediaQueries.sm} {
    height: 44px;
    font-size: 14px;
  }

  &:hover {
    svg path {
      fill: ${({ theme }) => theme.colors.textSubtle};
    }
  }
`

export default ProviderFilter
